/**
 * CLIENTS REQUEST. THE WORLD DECIDES.
 *
 * A `ClientIntent` is the only thing a client may put into the world, and this
 * module is the gate it comes through. Two rules decide the whole shape of the
 * file, and both are about what is deliberately ABSENT from a payload:
 *
 * **A client never names itself.** No payload carries a deity id. The server
 * knows whose hands these are because it knows which session the bytes arrived
 * on, and a session was bound to a deity when it authenticated. A `deityId`
 * field in a payload would be a field an attacker could set, so there is not
 * one — that is cheaper and more honest than validating one.
 *
 * **A client never names a time.** No payload carries a world time. The server
 * stamps every accepted intent with its own canonical clock. A client that
 * could choose when its act happened could reorder history, and ordering is
 * the one thing this simulation branches on hardest.
 *
 * What a payload carries is therefore only ever *where, what and how much* —
 * and each of those is bounded here before the simulation sees it. Everything
 * that comes back out of `validateIntent` is a value the `Simulation` is
 * already known to accept: finite, in range, and of a kind it has a verb for.
 *
 * ## Results are not intents
 *
 * There is deliberately no way to express "this animal died", "my power is
 * 1.0", "the fire spread here" or "I now hold 400 wood". Those are results,
 * and results are the world's. The nearest thing to an exception is
 * `presence`, which is documented at its own definition: it is where a god's
 * attention is, it is session state and never world history (law 9 —
 * observation writes nothing), and it is not journaled.
 *
 * ## Pure
 *
 * No node, no DOM, no simulation import except types and the focus law. The
 * server calls `validateIntent`; so could a browser wanting to check a payload
 * before sending it; so does `test/host-intent.ts`, with no server at all.
 */

import { FOCUS_BANDS, concentrationFor, type DivineFocus } from '@/sim/simulation';

/** Bump when a wire shape changes incompatibly. Clients send it on connect. */
export const PROTOCOL_VERSION = 1;

/**
 * The largest a single client message may be, in bytes of UTF-8. A divine act
 * is a few hundred bytes; four kilobytes is generous and a megabyte is an
 * attack.
 */
export const MAX_INTENT_BYTES = 4096;

/** The verbs a god may ask the world for. `Simulation.act`'s own union. */
export type ActVerb = 'strike' | 'rain' | 'strengthen' | 'weaken' | 'omen';
const ACT_VERBS: readonly string[] = ['strike', 'rain', 'strengthen', 'weaken', 'omen'];

/** The narrowest and widest cast the focus bands allow, in metres. */
export const MIN_CAST_RADIUS = FOCUS_BANDS[0].cast[0];
export const MAX_CAST_RADIUS = FOCUS_BANDS[FOCUS_BANDS.length - 1].cast[1];

/** The envelope every client message arrives in. */
export interface ClientIntent {
  /**
   * The client's own monotonically increasing counter. Used for acknowledgement
   * and for dropping a duplicate after a reconnect — NOT for ordering the
   * world, which is the server sequence's job. A client that resets this to
   * zero replays nothing; it only confuses its own acks.
   */
  seq: number;
  kind: string;
  payload?: unknown;
}

/* ── the validated forms ─────────────────────────────────────────────────── */

export interface ValidAct {
  kind: 'act';
  verb: ActVerb;
  x: number; z: number;
  focus: DivineFocus;
  /** Where the god is standing. Priced by `Simulation.act`; distance costs. */
  from?: { x: number; z: number };
  /** Share of the god to spend, 0..1. Absent means the default one stone. */
  spend?: number;
  target?: {
    kind: string; id: string;
    herd?: { species: string; sx: number; sz: number };
    plant?: { kind: string };
    aimed?: boolean;
  };
}

export interface ValidGrasp {
  kind: 'grasp';
  fromX: number; fromZ: number;
  toX: number; toZ: number;
}

export interface ValidAnchor {
  kind: 'anchor';
  x: number; z: number;
}

/**
 * Where this god's attention is — and the one intent that is not history.
 *
 * Law 9 and law 15: looking, following and caring write nothing, anywhere. So
 * a presence update mutates no simulation state, is never journaled, and never
 * appears in a checkpoint. It exists on the server for exactly two reasons:
 * other clients are shown where the other gods are looking, and `anchor` and
 * `grasp` are LOCAL acts whose legality depends on where the god actually is.
 *
 * Which is also the honest statement of its authority: a client does choose
 * its own presence, and cannot be stopped from doing so, because flying
 * anywhere instantly is a thing gods in this world may already do for free.
 * There is nothing to cheat. The moment presence costs something — travel
 * time, a body, an inhabited mortal — this stops being true and the server
 * will have to own the position. `SERVER.md` records that as the boundary.
 */
export interface ValidPresence {
  kind: 'presence';
  x: number; y: number; z: number;
  /** Ground height under the presence, metres. Used by the anchor's low test. */
  ground: number;
}

export type ValidIntent = ValidAct | ValidGrasp | ValidAnchor | ValidPresence;

/** Which validated kinds are external mutations that must be journaled. */
export const JOURNALED_KINDS: readonly ValidIntent['kind'][] = ['act', 'grasp', 'anchor'];
export const isJournaled = (k: ValidIntent['kind']): boolean =>
  JOURNALED_KINDS.includes(k);

/**
 * Kinds a client may plausibly try that this server does NOT yet own, with the
 * reason. Named rather than silently rejected as unknown, because §8's rule is
 * that unsupported authority is marked honestly rather than faked — a client
 * that asks for one of these gets told which, and why.
 */
export const UNSUPPORTED_KINDS: Readonly<Record<string, string>> = {
  inhabit: 'mortal embodiment is client-side by ruling; the server owns no mortal body yet',
  ascend: 'the other half of inhabit — same reason',
  earth: 'moveEarth/shapeLand are authoritative and implementable, but not validated ' +
         'or journaled in this pass',
  aura: 'kindleAura/douseAura hold power over world time; the refund and decay paths ' +
        'need their own authority review before a client may start one',
  build: 'construction is the mortals own doing; no player verb exists for it',
  combat: 'no combat system exists',
};

/**
 * WHAT A PLAYER MAY CALL THEMSELVES — and the sharp edge on it.
 *
 * A display name is **session metadata and nothing else**. It is not in the
 * `Pantheon`, not in any snapshot, not on the journal, and no mortal can ever
 * come near it. Law 10 is untouched: the world still cannot know whose hand
 * anything was, and this changes only what one PLAYER sees of another in the
 * chrome — which is a different question from what the world sees, and the
 * only reason a name is allowed to exist at all.
 *
 * Because it is the one string a stranger chooses and other people's browsers
 * display, it is bounded and stripped here rather than wherever it lands.
 * Control bytes go, because a name is written into a log line and a journal is
 * newline-delimited. Everything else is left alone and it is the RENDERER's
 * job to put it on screen as text rather than as markup — `textContent`, never
 * `innerHTML`. A validator that tried to make a name safe for `innerHTML` by
 * removing angle brackets would be teaching the wrong lesson to the next
 * person who displays one.
 */
export const MAX_NAME_LENGTH = 24;

export function cleanName(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const name = raw.replace(/[\u0000-\u001f\u007f]/g, '').trim();
  if (!name) return null;
  return name.length > MAX_NAME_LENGTH ? name.slice(0, MAX_NAME_LENGTH) : name;
}

export interface IntentLimits {
  /** Half the world's extent in metres: |x| and |z| must be inside it. */
  halfExtent: number;
}

export type ValidationResult =
  | { ok: true; intent: ValidIntent }
  | { ok: false; reason: string };

const bad = (reason: string): ValidationResult => ({ ok: false, reason });

const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** A coordinate inside the world, or null. */
const coord = (v: unknown, half: number): number | null =>
  finite(v) && Math.abs(v) <= half ? v : null;

/**
 * A short identifier a client may choose: bounded, and free of the control
 * bytes that would let a hostile id corrupt a journal line or a log.
 */
const ident = (v: unknown, max = 128): string | null => {
  if (typeof v !== 'string' || v.length === 0 || v.length > max) return null;
  return /[\u0000-\u001f\u007f]/.test(v) ? null : v;
};

/**
 * Turn whatever arrived off the wire into something the world will accept, or
 * say why not.
 *
 * Every branch is a whitelist. Nothing is copied through from the payload that
 * is not named here, so a field a future `Simulation` learns to read cannot
 * arrive from a client until somebody adds it to this file on purpose.
 */
export function validateIntent(raw: unknown, limits: IntentLimits): ValidationResult {
  if (!raw || typeof raw !== 'object') return bad('intent is not an object');
  const m = raw as Partial<ClientIntent> & Record<string, unknown>;
  if (!finite(m.seq) || m.seq < 0) return bad('seq must be a non-negative number');
  if (typeof m.kind !== 'string') return bad('kind must be a string');
  const p = (m.payload ?? {}) as Record<string, unknown>;
  const half = limits.halfExtent;

  if (Object.prototype.hasOwnProperty.call(UNSUPPORTED_KINDS, m.kind)) {
    return bad(`'${m.kind}' is not server-authoritative yet: ${UNSUPPORTED_KINDS[m.kind]}`);
  }

  switch (m.kind) {
    case 'act': {
      if (typeof p.verb !== 'string' || !ACT_VERBS.includes(p.verb)) {
        return bad(`verb must be one of ${ACT_VERBS.join(', ')}`);
      }
      const x = coord(p.x, half), z = coord(p.z, half);
      if (x === null || z === null) return bad('x/z must be finite and inside the world');
      // A radius, never a concentration. Concentration is DERIVED from radius by
      // the same law the client's reticle draws — `concentrationFor` — so a
      // client cannot ask for a watershed-sized cast at a tree's strength.
      const r = finite(p.radius) ? p.radius : NaN;
      if (!Number.isFinite(r) || r < MIN_CAST_RADIUS || r > MAX_CAST_RADIUS) {
        return bad(`radius must be ${MIN_CAST_RADIUS}..${MAX_CAST_RADIUS} m`);
      }
      const act: ValidAct = {
        kind: 'act', verb: p.verb as ActVerb, x, z,
        focus: { radius: r, concentration: concentrationFor(r) },
      };
      if (p.from !== undefined) {
        const f = p.from as Record<string, unknown>;
        const fx = coord(f?.x, half), fz = coord(f?.z, half);
        if (fx === null || fz === null) return bad('from.x/from.z must be inside the world');
        act.from = { x: fx, z: fz };
      }
      if (p.spend !== undefined) {
        if (!finite(p.spend) || p.spend < 0 || p.spend > 1) {
          return bad('spend must be 0..1');
        }
        act.spend = p.spend;
      }
      if (p.target !== undefined) {
        const t = p.target as Record<string, unknown>;
        const kind = ident(t?.kind, 32), id = ident(t?.id, 128);
        if (!kind || !id) return bad('target needs a short kind and id');
        const target: NonNullable<ValidAct['target']> = { kind, id };
        if (t.aimed !== undefined) {
          if (typeof t.aimed !== 'boolean') return bad('target.aimed must be a boolean');
          target.aimed = t.aimed;
        }
        if (t.herd !== undefined) {
          const h = t.herd as Record<string, unknown>;
          const species = ident(h?.species, 32);
          if (!species || !finite(h?.sx) || !finite(h?.sz)) {
            return bad('target.herd needs species and integer sx/sz');
          }
          target.herd = {
            species, sx: Math.trunc(h.sx as number), sz: Math.trunc(h.sz as number),
          };
        }
        if (t.plant !== undefined) {
          const pl = t.plant as Record<string, unknown>;
          const kindName = ident(pl?.kind, 32);
          if (!kindName) return bad('target.plant needs a kind');
          target.plant = { kind: kindName };
        }
        act.target = target;
      }
      return { ok: true, intent: act };
    }

    case 'grasp': {
      const fx = coord(p.fromX, half), fz = coord(p.fromZ, half);
      const tx = coord(p.toX, half), tz = coord(p.toZ, half);
      if (fx === null || fz === null || tx === null || tz === null) {
        return bad('grasp needs four finite coordinates inside the world');
      }
      return { ok: true, intent: { kind: 'grasp', fromX: fx, fromZ: fz, toX: tx, toZ: tz } };
    }

    case 'anchor': {
      const x = coord(p.x, half), z = coord(p.z, half);
      if (x === null || z === null) return bad('anchor needs x/z inside the world');
      // No presence in the payload, deliberately: the server anchors with the
      // presence IT last accepted from this session, so `too-far` and
      // `too-high` are decided against a position the client has already
      // declared and other clients have already been shown.
      return { ok: true, intent: { kind: 'anchor', x, z } };
    }

    case 'presence': {
      const x = coord(p.x, half), z = coord(p.z, half);
      if (x === null || z === null) return bad('presence needs x/z inside the world');
      if (!finite(p.y) || !finite(p.ground)) return bad('presence needs finite y and ground');
      // Height is bounded but not tightly: a god may fly. The bound exists so a
      // 1e300 cannot reach the anchor's arithmetic, not to police altitude.
      const y = Math.max(-1e5, Math.min(1e5, p.y as number));
      const ground = Math.max(-1e5, Math.min(1e5, p.ground as number));
      return { ok: true, intent: { kind: 'presence', x, y, z, ground } };
    }

    default:
      return bad(`unknown intent kind '${m.kind}'`);
  }
}
